import { getPageFromPath } from "../../utils/navUtils";
import {
  useGetPageFromPath,
  useMidgardIdHubletPage,
  useProcessesInVisibleGroups,
} from "../../utils/customHooks/genericHooks";
import { getTokenInfo } from "@raven/provisioning";
import { decodeToken } from "contexts/AuthManager/jwtHelper";
import { useWorkScheduleConfigJsonListAtom } from "store/atoms";

export function useShowWorkScheduleSwitcherDropdown() {
  const {
    isHubletPage,
    isGridLayoutPage,
    isOperatorPage,
    isProcessTilesPage,
    isTimelinePage,
    isGroupTilesPage,
    isReportsPage,
    isAllWorkSchedulesPage,
  } = useGetPageFromPath();
  const midgardIdHubletPage = useMidgardIdHubletPage();
  const [workSchedules] = useWorkScheduleConfigJsonListAtom();

  // if they only have 1 workschedule, hide it
  const numWorkSchedules = workSchedules.length;
  const hasOneWorkSchedule = numWorkSchedules === 1;

  if (
    hasOneWorkSchedule ||
    (isAllWorkSchedulesPage && isGroupTilesPage) ||
    midgardIdHubletPage
  ) {
    return false;
  }

  return (
    // otherwise, work schedule dropdown is visible on... hublet, timeline, process tiles, group tiles, grid, operator
    isHubletPage ||
    isOperatorPage ||
    isGridLayoutPage ||
    isProcessTilesPage ||
    isTimelinePage ||
    isReportsPage ||
    isGroupTilesPage
  );
}

export function useShowProcessGroupSwitcherDropdown() {
  const {
    isHubletPage,
    isGridLayoutPage,
    isOperatorPage,
    isProcessTilesPage,
    isTimelinePage,
    isTier0Page,
    isAllWorkSchedulesPage,
    isGroupTilesPage,
  } = useGetPageFromPath();
  const midgardIdHubletPage = useMidgardIdHubletPage();
  if ((isAllWorkSchedulesPage && isGroupTilesPage) || midgardIdHubletPage) {
    return false;
  }

  return (
    // machine switcher is available on single-machine pages
    isHubletPage ||
    isOperatorPage ||
    isGridLayoutPage ||
    isProcessTilesPage ||
    isTimelinePage ||
    isTier0Page
  );
}

export function useShowMachineSwitcherDropdown() {
  const { isHubletPage, isGridLayoutPage, isOperatorPage, isTier0Page } =
    useGetPageFromPath();
  const midgardIdHubletPage = useMidgardIdHubletPage();

  return (
    // machine switcher is available on single-machine pages
    isHubletPage ||
    isOperatorPage ||
    isGridLayoutPage ||
    isTier0Page ||
    midgardIdHubletPage
  );
}

export function useShowMachineSwitcherDropdownArrow() {
  const processesInVisibleGroups = useProcessesInVisibleGroups();

  return (
    // we have to have multiple processes to switch between
    processesInVisibleGroups.length > 1
  );
}

export function setBackButtonLocation(
  lastLocation: any,
  pathname: string,
  lastLocationPathStored: string,
  setLastLocationPathStored: React.Dispatch<
    React.SetStateAction<string | undefined>
  >,
  autoRedirectIntoFirstGroup: boolean,
  ckey: string
) {
  let nextLastLocation = "";
  if (lastLocation) {
    const lastLocationFullPath = `${lastLocation.pathname}${lastLocation.search}`;
    const { isHubletPage: isLastLocationDetailsPage } = getPageFromPath({
      pathnameAndSearch: lastLocationFullPath,
      isTest: true,
    });
    const currentLocationFullPath = `${window.location.pathname}${window.location.search}`;
    const { isOperatorPage: isCurrentLocationOperatorPage } = getPageFromPath({
      pathnameAndSearch: currentLocationFullPath,
      isTest: true,
    });
    if (
      // store current path as back link
      // EXCEPTIONS: don't ever link back to
      // settings, help, details
      // go up instead
      !lastLocation.pathname.includes("settings") &&
      !lastLocation.pathname.includes("help") &&
      !lastLocation.pathname.includes("callback") &&
      !lastLocation.search.includes("code=") &&
      !(
        isLastLocationDetailsPage &&
        !isCurrentLocationOperatorPage &&
        !pathname.includes("help") &&
        !pathname.includes("settings")
      ) &&
      !pathname.includes("help") &&
      lastLocationFullPath !== lastLocationPathStored
    ) {
      nextLastLocation = lastLocationFullPath;
    }
  }
  // if we're auto-redirecting to skip group tiles page, and last location would be group tiles page, instead it should be schedule page

  if (autoRedirectIntoFirstGroup && nextLastLocation.includes("/dashboard?")) {
    nextLastLocation = `${ckey}/schedule`;
  }
  if (nextLastLocation) {
    setLastLocationPathStored(nextLastLocation);
  }
}

export function getCustomersInToken(t?: string) {
  const { idToken } = getTokenInfo();
  const token = t ?? idToken;
  const decoded = token && decodeToken(token);
  const customersInToken =
    decoded?.["https://raven.ai/app_metadata"]?.customer ??
    decoded?.app_metadata?.customer ??
    decoded?.customer ??
    [];
  return customersInToken;
}
